import { useTranslation } from 'react-i18next'

import {
  ProductModalProductPreviewLineFragment,
  ProductModalProductPreviewProductFragment,
  ProductModalProductPreviewSupplierFragment,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Input as FormatProductInput, formatProductItem } from '@/modules/requisitions/utils/formatProductItem'
import { ImageItem } from '@/modules/shared/components/image/ImageItem'
import PreferredSupplier from '@/modules/shared/components/product-list/PreferredSupplier'
import { useMoney } from '@/modules/shared/hooks/useMoney'

interface ProductModalProductPreviewProps {
  product?: ProductModalProductPreviewProductFragment | null
  formatProduct?: (input: FormatProductInput) => string
  line?: ProductModalProductPreviewLineFragment | null
  supplier?: ProductModalProductPreviewSupplierFragment | null
  image?: string | null
}

export default function ProductModalProductPreview({
  product,
  formatProduct,
  line,
  supplier,
  image,
}: ProductModalProductPreviewProps) {
  const {
    brand,
    itemDescription,
    itemSize,
    itemMeasure,
    itemPackName,
    concatenatedSellUnit,
    itemSellQuantity,
    longDescription,
  } = product || {}
  const { unitPrice, taxPercentage } = line || {}
  const { formatMoney } = useMoney()
  const { t } = useTranslation()

  return (
    <>
      <section data-testid="product-info" className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <ImageItem className="size-full flex-none border sm:size-64" src={image || product?.image} alt="product" />
        <div className="flex flex-col gap-4">
          <span>
            <strong className="text-base">
              {brand} {itemDescription}
            </strong>
            <p className="text-sm text-gray-500">
              {formatProduct
                ? formatProduct({
                    itemSize,
                    itemMeasure,
                    itemPackName,
                    itemSellUnit: concatenatedSellUnit,
                    itemSellQuantity,
                  })
                : formatProductItem({
                    itemSize,
                    itemMeasure,
                    itemPackName,
                    itemSellUnit: concatenatedSellUnit,
                    itemSellQuantity,
                  })}
            </p>
          </span>
          {(unitPrice != null || taxPercentage != null) && (
            <span>
              <strong className="text-2xl">{formatMoney({ amount: unitPrice })}</strong>
              {taxPercentage !== null && (
                <p className="text-xs text-gray-500">
                  {taxPercentage
                    ? t('general.plusTax', 'Plus {{ taxPercentage }}% Tax', {
                        taxPercentage,
                      })
                    : t('general.taxFree', 'Tax Free')}
                </p>
              )}
            </span>
          )}
          {supplier && (
            <span data-testid="product-info-supplier">
              <strong className="text-sm">{t('general.supplier', 'Supplier')}</strong>
              <PreferredSupplier
                preferredSupplier={!!supplier?.supplierRelationship?.preferredSupplier}
                supplierName={supplier?.name}
                supplierRelationShipId={supplier?.supplierRelationship?.id}
                supplierId={supplier?.id}
              />
            </span>
          )}
        </div>
      </section>
      <div className="mt-4">
        <strong className="text-sm">{t('general.description', 'Description')}</strong>
        <div className="text-sm">
          {longDescription || (
            <p className="mt-2 border py-4 text-center text-gray-500">
              {t(
                'shopPage.productModal.productDetails.noLongDescription',
                'The Owner of this product has not added a description.'
              )}
            </p>
          )}
        </div>
      </div>
    </>
  )
}
